import React, { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useLang, LanguageSwitcher } from "@/lib/lang";
import { LayoutDashboard, Users, Car, Building2, Handshake, Map, Route, Ticket, Star, Wallet, Settings, FileCheck2, UserCircle, LogOut, ChevronRight, ChevronDown, Plug, Scale, Cog, Code2, Globe, ShieldCheck, ShieldOff, CalendarRange, KeyRound, FileSignature, CalendarClock, MapPin, Calendar as CalendarIcon, MessageCircle } from "lucide-react";

const NAV_ITEMS = [
  { to: "/admin", key: "nav_dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/bookings", key: "nav_bookings", icon: Ticket },
  { to: "/admin/calendar", key: "nav_calendar", icon: CalendarRange },
  { to: "/admin/drivers", key: "nav_drivers", icon: Users },
  { to: "/admin/vehicles", key: "nav_vehicles", icon: Car },
  { to: "/admin/vehicle-categories", key: "nav_vehicle_categories", icon: Cog },
  { to: "/admin/fleets", key: "nav_fleets", icon: Building2 },
  { to: "/admin/partners", key: "nav_partners", icon: Handshake },
  { to: "/admin/approvals", key: "nav_approvals", icon: ShieldCheck },
  { to: "/admin/blocked", key: "nav_blocked", icon: ShieldOff },
  { to: "/admin/tours", key: "nav_tours", icon: Map },
  { to: "/admin/routes", key: "nav_routes", icon: Route },
  { to: "/admin/zones", key: "nav_zones", icon: MapPin },
  { to: "/admin/seasons", key: "nav_seasons", icon: CalendarIcon },
  { to: "/admin/documents", key: "nav_documents", icon: FileCheck2 },
  { to: "/admin/clients", key: "nav_clients", icon: UserCircle },
  { to: "/admin/reviews", key: "nav_reviews", icon: Star },
  { to: "/admin/finance", key: "nav_financial", icon: Wallet },
];

const SETTINGS_ITEMS = [
  { to: "/admin/settings/integrations", key: "nav_integrations", icon: Plug },
  { to: "/admin/settings/legal", key: "nav_legal", icon: Scale },
  { to: "/admin/fees", key: "nav_fees", icon: Wallet },
  { to: "/admin/settings/system", key: "nav_system", icon: Cog },
  { to: "/admin/settings/dispatch", key: "nav_dispatch", icon: CalendarClock },
  { to: "/admin/settings/whatsapp", key: "nav_whatsapp", icon: MessageCircle },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const { t } = useLang();
  const nav = useNavigate();
  const [settingsOpen, setSettingsOpen] = useState(true);

  return (
    <div className="admin-shell min-h-screen bg-white flex">
      {/* Sidebar */}
      <aside className="w-64 border-r border-slate-200 fixed h-screen flex flex-col bg-[#fafafa] z-30">
        <div className="p-6 border-b border-slate-200 bg-white">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 border border-[#f59e0b] flex items-center justify-center">
              <span className="font-display text-[#f59e0b] text-lg">D</span>
            </div>
            <div>
              <div className="font-display text-xl text-[#0a0f1e] leading-none">DiTours</div>
              <div className="label-caps mt-1 text-[9px]">{t("nav_console")}</div>
            </div>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto py-3">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={`nav-${item.key}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-6 py-2.5 text-sm transition-colors border-l-2 ${
                  isActive
                    ? "border-[#f59e0b] text-[#0a0f1e] bg-white font-semibold"
                    : "border-transparent text-slate-600 hover:text-[#0a0f1e] hover:bg-white"
                }`
              }
            >
              <item.icon className="w-4 h-4" strokeWidth={1.5} />
              <span className="tracking-wide">{t(item.key)}</span>
            </NavLink>
          ))}

          {/* Settings group */}
          <button
            data-testid="nav-settings-group"
            onClick={() => setSettingsOpen(!settingsOpen)}
            className="w-full flex items-center justify-between px-6 py-2.5 mt-2 text-sm text-slate-600 hover:text-[#0a0f1e] transition-colors border-l-2 border-transparent"
          >
            <span className="flex items-center gap-3">
              <Settings className="w-4 h-4" strokeWidth={1.5} />
              <span className="tracking-wide">{t("nav_settings")}</span>
            </span>
            {settingsOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
          </button>
          {settingsOpen && SETTINGS_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-${item.key}`}
              className={({ isActive }) =>
                `flex items-center gap-3 pl-14 pr-6 py-2 text-xs transition-colors border-l-2 ${
                  isActive
                    ? "border-[#f59e0b] text-[#f59e0b] bg-white font-semibold"
                    : "border-transparent text-slate-500 hover:text-[#0a0f1e]"
                }`
              }
            >
              <item.icon className="w-3.5 h-3.5" strokeWidth={1.5} />
              <span className="tracking-wide">{t(item.key)}</span>
            </NavLink>
          ))}
        </nav>

        <div className="p-6 border-t border-slate-200">
          <div className="flex items-center gap-2 mb-3">
            <Globe className="w-3.5 h-3.5 text-slate-500" />
            <LanguageSwitcher testId="admin-lang-switch" />
          </div>
          <div className="text-xs text-slate-500 label-caps mb-1">{t("signed_in_as")}</div>
          <div className="text-sm text-[#0a0f1e] truncate">{user?.name}</div>
          <div className="text-xs text-slate-500 truncate">{user?.email}</div>
          <button
            data-testid="logout-btn"
            onClick={async () => { await logout(); nav("/login"); }}
            className="mt-4 flex items-center gap-2 text-xs text-slate-500 hover:text-[#f59e0b] transition-colors"
          >
            <LogOut className="w-3.5 h-3.5" /> {t("sign_out")}
          </button>
        </div>
      </aside>

      <main className="ml-64 flex-1 min-h-screen">
        <Outlet />
      </main>
    </div>
  );
}

export function PageHeader({ eyebrow, title, description, children }) {
  return (
    <div className="flex items-end justify-between mb-10 pb-6 border-b border-slate-200">
      <div>
        {eyebrow && <p className="label-caps text-[#f59e0b] mb-2">{eyebrow}</p>}
        <h1 className="font-display text-4xl text-[#0a0f1e] tracking-tight">{title}</h1>
        {description && <p className="text-slate-500 mt-2 text-sm max-w-2xl">{description}</p>}
      </div>
      {children && <div className="flex items-center gap-3">{children}</div>}
    </div>
  );
}

// Enum -> i18n key mapping for common status values across the app.
// Falls back to the raw enum (space-separated) when no key is registered.
const STATUS_I18N_KEYS = {
  pending_payment: "status_pending_payment",
  confirmed: "status_confirmed",
  assigned: "status_assigned",
  in_progress: "status_in_progress",
  completed: "status_completed",
  cancelled: "status_cancelled",
  refund_pending: "status_refund_pending",
  refunded: "status_refunded",
};

export function StatBadge({ status }) {
  const { t } = useLang();
  const s = String(status || "").toLowerCase();
  const map = {
    approved: "text-emerald-700 border-emerald-500/30 bg-emerald-50",
    active: "text-emerald-700 border-emerald-500/30 bg-emerald-50",
    completed: "text-emerald-700 border-emerald-500/30 bg-emerald-50",
    paid: "text-emerald-700 border-emerald-500/30 bg-emerald-50",
    valid: "text-emerald-700 border-emerald-500/30 bg-emerald-50",
    pending: "text-amber-700 border-amber-500/30 bg-amber-50",
    pending_payment: "text-amber-700 border-amber-500/30 bg-amber-50",
    processing: "text-amber-700 border-amber-500/30 bg-amber-50",
    confirmed: "text-sky-700 border-sky-500/30 bg-sky-50",
    assigned: "text-sky-700 border-sky-500/30 bg-sky-50",
    in_progress: "text-[#b45309] border-[#f59e0b]/40 bg-[#f59e0b]/10",
    expiring: "text-amber-700 border-amber-500/30 bg-amber-50",
    expired: "text-red-700 border-red-500/30 bg-red-50",
    suspended: "text-red-700 border-red-500/30 bg-red-50",
    rejected: "text-red-700 border-red-500/30 bg-red-50",
    cancelled: "text-red-700 border-red-500/30 bg-red-50",
    refund_pending: "text-orange-700 border-orange-500/30 bg-orange-50",
    refunded: "text-slate-600 border-slate-300 bg-slate-50",
  };
  const cls = map[s] || "text-slate-600 border-slate-300 bg-slate-50";
  const key = STATUS_I18N_KEYS[s];
  const label = key ? t(key) : String(status || "").replace(/_/g, " ");
  return (
    <span className={`inline-flex items-center px-2 py-0.5 text-[10px] uppercase tracking-wider border ${cls}`}>
      {label}
    </span>
  );
}

export function GoldButton(props) {
  return (
    <button
      {...props}
      className={`inline-flex items-center gap-2 h-10 px-5 bg-[#f59e0b] text-white font-semibold text-xs tracking-wide uppercase hover:bg-[#d97706] transition-colors disabled:opacity-40 shadow-sm ${props.className || ""}`}
    />
  );
}

export function GhostButton(props) {
  return (
    <button
      {...props}
      className={`inline-flex items-center gap-2 h-10 px-4 border border-slate-300 text-slate-700 text-xs uppercase tracking-wide hover:border-[#f59e0b] hover:text-[#f59e0b] transition-colors ${props.className || ""}`}
    />
  );
}
